import React, { useState, useEffect, useRef, useCallback } from 'react';
import { api } from '../services/api';
import ConfirmModal from './ConfirmModal';

const GRID          = 10;
const MIN_TABLE     = 60;
const MIN_ELEM      = 20;
const CANVAS_W      = 1400;
const CANVAS_H      = 900;
const EDIT_CANVAS_W = 4000;
const EDIT_CANVAS_H = 3000;
const EDIT_OFFSET_X = (EDIT_CANVAS_W - CANVAS_W) / 2;  // 1300 — space to scroll left
const EDIT_OFFSET_Y = (EDIT_CANVAS_H - CANVAS_H) / 2;  // 1050 — space to scroll up
const DOOR_SIZE     = 54;
const VIEW_PAD      = 60;   // px past last table in view mode

const LOCATIONS = ['indoor','outdoor','bar','terrace','first floor','second floor','private','rooftop','patio'];
const LOC_COLOR = {
  'indoor':'#4f46e5','outdoor':'#059669','bar':'#7c3aed','terrace':'#d97706',
  'first floor':'#0891b2','second floor':'#be123c','private':'#6366f1',
  'rooftop':'#b45309','patio':'#65a30d',
};

function snap(v)   { return Math.round(v / GRID) * GRID; }
function uid()     { return Date.now().toString(36) + Math.random().toString(36).slice(2,6); }
function fmt(t)    { const [h,m]=t.split(':').map(Number); return `${h%12||12}:${String(m).padStart(2,'0')} ${h>=12?'PM':'AM'}`; }

// Project point (px,py) onto segment (x1,y1)-(x2,y2); returns {t,dist}
function projectOnSeg(px,py,x1,y1,x2,y2) {
  const dx=x2-x1, dy=y2-y1, len2=dx*dx+dy*dy;
  if (len2===0) return { t:0, dist:Math.hypot(px-x1,py-y1) };
  const t=Math.max(0,Math.min(1,((px-x1)*dx+(py-y1)*dy)/len2));
  return { t, dist:Math.hypot(px-(x1+t*dx),py-(y1+t*dy)) };
}

// Returns the size of the largest connected wall component.
// Two walls are "connected" when any endpoint pair is within SNAP px.
// A meaningful room needs at least 3 connected walls forming an enclosure.
function largestConnectedWalls(elements) {
  const walls = elements.filter(e => e.type === 'wall');
  if (!walls.length) return 0;
  const SNAP = 22;
  const adj = walls.map(() => []);
  for (let i = 0; i < walls.length; i++) {
    for (let j = i + 1; j < walls.length; j++) {
      const a = walls[i], b = walls[j];
      const pts = [[a.x1,a.y1,b.x1,b.y1],[a.x1,a.y1,b.x2,b.y2],[a.x2,a.y2,b.x1,b.y1],[a.x2,a.y2,b.x2,b.y2]];
      if (pts.some(([ax,ay,bx,by]) => Math.hypot(ax-bx,ay-by) < SNAP)) {
        adj[i].push(j); adj[j].push(i);
      }
    }
  }
  const visited = new Set();
  let max = 0;
  for (let i = 0; i < walls.length; i++) {
    if (visited.has(i)) continue;
    const stack = [i]; let size = 0;
    while (stack.length) {
      const n = stack.pop();
      if (visited.has(n)) continue;
      visited.add(n); size++;
      adj[n].forEach(nb => stack.push(nb));
    }
    max = Math.max(max, size);
  }
  return max;
}

// Returns a Set of table IDs that are within `threshold` px of any window segment
function getWindowSeatIds(tables, elements, threshold = 90) {
  const ids = new Set();
  for (const wall of elements) {
    if (wall.type !== 'wall' || !wall.windows?.length) continue;
    const dx = wall.x2 - wall.x1, dy = wall.y2 - wall.y1;
    for (const win of wall.windows) {
      const wx1 = wall.x1 + win.t1 * dx, wy1 = wall.y1 + win.t1 * dy;
      const wx2 = wall.x1 + win.t2 * dx, wy2 = wall.y1 + win.t2 * dy;
      for (const t of tables) {
        if (!t.floor_plan_placed) continue;
        const { dist } = projectOnSeg(t.x + t.w/2, t.y + t.h/2, wx1, wy1, wx2, wy2);
        if (dist <= threshold) ids.add(t.id);
      }
    }
  }
  return ids;
}

// Find first free position that doesn't overlap placed tables or solid elements
function findFreePosition(placedTables, elements, newW=80, newH=80) {
  const GAP     = 14;
  const CX      = CANVAS_W / 2;   // 700 — blue dot x
  const CY      = CANVAS_H / 2;   // 450 — blue dot y
  const colStep = snap(newW + GAP);
  const rowStep = snap(newH + GAP);

  const obstacles = [
    ...placedTables.map(t => ({ x:t.x, y:t.y, w:t.w, h:t.h })),
    ...elements.filter(e => e.type==='box'||e.type==='stairs')
               .map(e => ({ x:e.x, y:e.y, w:e.w, h:e.h })),
  ];
  const overlaps = (cx, cy) => obstacles.some(o =>
    cx < o.x+o.w+GAP && cx+newW+GAP > o.x &&
    cy < o.y+o.h+GAP && cy+newH+GAP > o.y
  );

  // Build a grid of candidates centred on the blue dot, sorted by distance from it.
  // Range: enough rings to cover the full edit canvas if needed.
  const candidates = [];
  for (let row = -30; row <= 30; row++) {
    for (let col = -30; col <= 30; col++) {
      const x = snap(CX - newW / 2 + col * colStep);
      const y = snap(CY - newH / 2 + row * rowStep);
      if (x < 0 || y < 0) continue;
      candidates.push({ x, y, d: col*col + row*row });
    }
  }
  candidates.sort((a, b) => a.d - b.d);

  for (const { x, y } of candidates) {
    if (!overlaps(x, y)) return { x, y };
  }
  return { x: snap(CX - newW / 2), y: snap(CY - newH / 2) };
}

// Snap wall endpoint to nearby other wall endpoint; returns new elems array or null
function snapWallEndpoint(elems,wallId,part) {
  const wall=elems.find(e=>e.id===wallId);
  if (!wall) return null;
  const px=part==='p1'?wall.x1:wall.x2, py=part==='p1'?wall.y1:wall.y2;
  for (const other of elems) {
    if (other.type!=='wall'||other.id===wallId) continue;
    for (const [ox,oy] of [[other.x1,other.y1],[other.x2,other.y2]]) {
      if (Math.hypot(px-ox,py-oy)<15)
        return elems.map(e=>e.id===wallId?{...e,...(part==='p1'?{x1:ox,y1:oy}:{x2:ox,y2:oy})}:e);
    }
  }
  return null;
}

export default function FloorPlan({ isActive = true }) {
  // ── tables ────────────────────────────────────────────────────────────────────
  const [tables,       setTables]       = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [editMode,     setEditMode]     = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [saved,        setSaved]        = useState(false);
  const [showAdd,      setShowAdd]      = useState(false);
  const [newTable,     setNewTable]     = useState({ label:'', capacity:4, location:'indoor' });
  const [resoStatus,   setResoStatus]   = useState({});
  const [viewTable,    setViewTable]    = useState(null);
  const [tableResos,   setTableResos]   = useState([]);
  const [loadingResos, setLoadResos]    = useState(false);
  const [booking,      setBooking]      = useState(null);
  const [bookingTables,setBookingTables]= useState([]);
  const [bookingChecking,setBookingChecking]= useState(false);
  const [bookingError, setBookingError] = useState('');
  const [bookingSuccess,setBookingSuccess]= useState('');
  const [bookingSaving,setBookingSaving]= useState(false);
  const [editSel,      setEditSel]      = useState(null);
  const [panelEdit,    setPanelEdit]    = useState({});
  const [activeArea,   setActiveArea]   = useState(null);

  const [aiEnabled,    setAiEnabled]    = useState(false);

  // ── decorative elements ───────────────────────────────────────────────────────
  const [elements,     setElements]     = useState([]);
  const [editSelElem,  setEditSelElem]  = useState(null);
  const [elemPanelEdit,setElemPanelEdit]= useState({});

  const [zoom,         setZoom]         = useState(1);
  const [confirm,      setConfirm]      = useState(null);

  const handleZoom = useCallback((newZoom) => {
    if (!wrapperRef.current) { setZoom(newZoom); return; }
    const z = zoomRef.current;
    const { scrollLeft, scrollTop, clientWidth, clientHeight } = wrapperRef.current;
    const centerX = (scrollLeft + clientWidth  / 2) / z;
    const centerY = (scrollTop  + clientHeight / 2) / z;
    setZoom(newZoom);
    requestAnimationFrame(() => {
      if (wrapperRef.current) {
        wrapperRef.current.scrollLeft = centerX * newZoom - wrapperRef.current.clientWidth  / 2;
        wrapperRef.current.scrollTop  = centerY * newZoom - wrapperRef.current.clientHeight / 2;
      }
    });
  }, []);

  // ── refs ──────────────────────────────────────────────────────────────────────
  const dragRef      = useRef(null);
  const tablesRef    = useRef(tables);   tablesRef.current    = tables;
  const elementsRef  = useRef(elements); elementsRef.current  = elements;
  const zoomRef      = useRef(zoom);     zoomRef.current      = zoom;
  const wrapperRef   = useRef(null);
  const canvasWRef   = useRef(CANVAS_W);
  const canvasHRef   = useRef(CANVAS_H);
  const editModeRef  = useRef(editMode); editModeRef.current  = editMode;
  const today        = new Date().toISOString().slice(0,10);

  // ── load ─────────────────────────────────────────────────────────────────────
  const loadResoStatus = useCallback(async () => {
    try {
      const resos  = await api.getReservations({});
      const status = {};
      for (const r of resos) {
        if (r.status==='cancelled' || r.date<today) continue;
        const ids = [];
        if (r.table_id) ids.push(r.table_id);
        if (r.combined_tables) { try { JSON.parse(r.combined_tables).forEach(id=>ids.push(id)); } catch(_){} }
        for (const id of ids) {
          if (r.date===today) status[id]='today';
          else if (!status[id]) status[id]='upcoming';
        }
      }
      setResoStatus(status);
    } catch(_) {}
  }, [today]);

  const loadTables = useCallback(async () => {
    try {
      const t = await api.getTables();
      setTables(t.map(tbl => ({ ...tbl, x: tbl.x||50, y: tbl.y||50, w: tbl.w||80, h: tbl.h||80, shape: tbl.shape||'rect' })));
    } catch(_) {}
  }, []);

  useEffect(() => {
    Promise.all([api.getTables(), api.getSettings(), loadResoStatus()]).then(([t, settings]) => {
      setTables(t.map(tbl => ({ ...tbl, x: tbl.x||50, y: tbl.y||50, w: tbl.w||80, h: tbl.h||80, shape: tbl.shape||'rect' })));
      try {
        const raw = settings?.floor_plan_elements;
        if (raw) setElements(typeof raw==='string' ? JSON.parse(raw) : (Array.isArray(raw) ? raw : []));
      } catch(_) {}
      setAiEnabled(settings?.floor_plan_ai_enabled === 'true');
      setLoading(false);
    });
  }, [loadResoStatus]);

  useEffect(() => {
    const handler = () => { loadResoStatus(); loadTables(); };
    window.addEventListener('seva:refresh', handler);
    return () => window.removeEventListener('seva:refresh', handler);
  }, [loadResoStatus, loadTables]);

  // Center on the blue dot in view mode.
  // Uses canvasWRef/canvasHRef (set during render) so it works with the symmetric canvas.
  // Double-rAF ensures we run AFTER React has finished painting.
  const scrollToViewCenter = useCallback(() => {
    requestAnimationFrame(() => requestAnimationFrame(() => {
      if (!wrapperRef.current) return;
      const { clientWidth, clientHeight } = wrapperRef.current;
      const z = zoomRef.current;
      // dot is always at canvasW/2, canvasH/2 (symmetric canvas centred on it)
      wrapperRef.current.scrollLeft = Math.max(0, canvasWRef.current / 2 * z - clientWidth  / 2);
      wrapperRef.current.scrollTop  = Math.max(0, canvasHRef.current / 2 * z - clientHeight / 2);
    }));
  }, []);

  // Scroll to centre whenever the tab becomes visible or data finishes loading.
  // Component stays mounted (display:none when inactive) so we need the isActive check.
  useEffect(() => {
    if (isActive && !loading) scrollToViewCenter();
  }, [isActive, loading]); // eslint-disable-line

  useEffect(() => {
    if (!editMode) {
      setEditSel(null); setPanelEdit({}); setEditSelElem(null);
      scrollToViewCenter();
    } else {
      // Scroll so the center of the working area (where the dot is) appears centered in the viewport
      requestAnimationFrame(() => {
        if (wrapperRef.current) {
          const { clientWidth, clientHeight } = wrapperRef.current;
          const z = zoomRef.current;
          wrapperRef.current.scrollLeft = (EDIT_OFFSET_X + CANVAS_W / 2 - clientWidth  / 2) * z;
          wrapperRef.current.scrollTop  = (EDIT_OFFSET_Y + CANVAS_H / 2 - clientHeight / 2) * z;
        }
      });
    }
  }, [editMode]);

  useEffect(() => {
    const tbl = tables.find(t => t.id===editSel);
    if (tbl) setPanelEdit({ label:tbl.label, capacity:tbl.capacity, location:tbl.location });
  }, [editSel]); // eslint-disable-line

  useEffect(() => {
    const elem = elements.find(e => e.id===editSelElem);
    if (elem) setElemPanelEdit({ label: elem.label||'' });
  }, [editSelElem]); // eslint-disable-line

  // Keyboard Delete / Backspace to remove selected item
  useEffect(() => {
    if (!editMode) return;
    const onKey = (e) => {
      if (e.key !== 'Delete' && e.key !== 'Backspace') return;
      if (['INPUT','TEXTAREA','SELECT'].includes(e.target.tagName)) return;
      e.preventDefault();
      if (editSel) removeTable(editSel);
      else if (editSelElem) removeElement(editSelElem);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [editMode, editSel, editSelElem]); // eslint-disable-line

  // ── save ─────────────────────────────────────────────────────────────────────
  const savePositions = useCallback(async (ts) => {
    setSaving(true);
    try {
      await api.saveFloorPlan(ts.map(t => ({ id:t.id, x:t.x, y:t.y, w:t.w, h:t.h })));
      setSaved(true); setTimeout(()=>setSaved(false),2000);
    } catch(_) {} finally { setSaving(false); }
  }, []);

  const saveElements = useCallback(async (elems) => {
    try { await api.updateSettings({ floor_plan_elements: JSON.stringify(elems) }); } catch(_) {}
  }, []);

  // ── drag (tables) ─────────────────────────────────────────────────────────────
  const onPointerDown = useCallback((e, id, type) => {
    e.preventDefault(); e.stopPropagation();
    setEditSel(id); setEditSelElem(null);
    const tbl = tablesRef.current.find(t => t.id===id);
    dragRef.current = { kind:'table', id, type, startX:e.clientX, startY:e.clientY,
      origX:tbl.x, origY:tbl.y, origW:tbl.w, origH:tbl.h };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  // ── drag (elements) ───────────────────────────────────────────────────────────
  const onElemPointerDown = useCallback((e, id, part) => {
    e.preventDefault(); e.stopPropagation();
    setEditSelElem(id); setEditSel(null);
    const elem = elementsRef.current.find(el => el.id===id);
    dragRef.current = { kind:'element', id, part, startX:e.clientX, startY:e.clientY, orig:{ ...elem } };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  // ── drag (window / door segments on walls) ───────────────────────────────────
  const onWinPointerDown = useCallback((e, elemId, winId, action) => {
    e.preventDefault(); e.stopPropagation();
    setEditSelElem(elemId); setEditSel(null);
    const elem = elementsRef.current.find(el => el.id===elemId);
    dragRef.current = { kind:'element', id:elemId, part:'win', winId, winAction:action,
      startX:e.clientX, startY:e.clientY, orig:{ ...elem, windows:[...(elem.windows||[])] } };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onDoorPointerDown = useCallback((e, elemId, doorId, action) => {
    e.preventDefault(); e.stopPropagation();
    setEditSelElem(elemId); setEditSel(null);
    const elem = elementsRef.current.find(el => el.id===elemId);
    dragRef.current = { kind:'element', id:elemId, part:'door', winId:doorId, winAction:action,
      startX:e.clientX, startY:e.clientY, orig:{ ...elem, doors:[...(elem.doors||[])] } };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  const onToiletDoorPointerDown = useCallback((e, elemId, doorId, side, action) => {
    e.preventDefault(); e.stopPropagation();
    setEditSelElem(elemId); setEditSel(null);
    const elem = elementsRef.current.find(el => el.id===elemId);
    dragRef.current = { kind:'toiletdoor', id:elemId, doorId, side, action,
      startX:e.clientX, startY:e.clientY, orig:{ ...elem, doors:[...(elem.doors||[])] } };
    e.currentTarget.setPointerCapture(e.pointerId);
  }, []);

  // ── unified move / up ────────────────────────────────────────────────────────
  const onPointerMove = useCallback((e) => {
    if (!dragRef.current) return;

    // Auto-scroll when dragging near wrapper edges in edit mode
    if (editModeRef.current && wrapperRef.current) {
      const rect = wrapperRef.current.getBoundingClientRect();
      const px = e.clientX - rect.left;
      const py = e.clientY - rect.top;
      const MARGIN = 50, SPEED = 8;
      if (px < MARGIN)                    { wrapperRef.current.scrollLeft -= SPEED; dragRef.current.startX += SPEED; }
      else if (px > rect.width - MARGIN)  { wrapperRef.current.scrollLeft += SPEED; dragRef.current.startX -= SPEED; }
      if (py < MARGIN)                    { wrapperRef.current.scrollTop  -= SPEED; dragRef.current.startY += SPEED; }
      else if (py > rect.height - MARGIN) { wrapperRef.current.scrollTop  += SPEED; dragRef.current.startY -= SPEED; }
    }

    const { kind } = dragRef.current;
    const z = zoomRef.current;
    const dx = (e.clientX - dragRef.current.startX) / z;
    const dy = (e.clientY - dragRef.current.startY) / z;

    if (kind==='table') {
      const { id, type, origX, origY, origW, origH } = dragRef.current;
      setTables(prev => prev.map(t => {
        if (t.id!==id) return t;
        if (type==='move')   return { ...t, x:snap(Math.max(0,origX+dx)), y:snap(Math.max(0,origY+dy)) };
        if (type==='resize') {
          if ((t.shape||'rect')==='round') { const ns=snap(Math.max(MIN_TABLE,Math.max(origW+dx,origH+dy))); return {...t,w:ns,h:ns}; }
          return { ...t, w:snap(Math.max(MIN_TABLE,origW+dx)), h:snap(Math.max(MIN_TABLE,origH+dy)) };
        }
        return t;
      }));
    } else if (kind==='element') {
      const { id, part, orig } = dragRef.current;

      // Window drag
      if (part==='win') {
        const { winId, winAction } = dragRef.current;
        const wdx = orig.x2-orig.x1, wdy = orig.y2-orig.y1;
        const wallLen2 = wdx*wdx + wdy*wdy;
        if (wallLen2 === 0) return;
        const dt = (dx*wdx + dy*wdy) / wallLen2;
        const win = (orig.windows||[]).find(w => w.id===winId);
        if (!win) return;
        const MIN_WIN = 30 / Math.sqrt(wallLen2);
        let t1 = win.t1, t2 = win.t2;
        if (winAction==='mv') {
          const span = win.t2-win.t1;
          t1 = Math.max(0, Math.min(1-span, win.t1+dt));
          t2 = t1+span;
        } else if (winAction==='t1') {
          t1 = Math.max(0, Math.min(win.t2-MIN_WIN, win.t1+dt));
        } else if (winAction==='t2') {
          t2 = Math.min(1, Math.max(win.t1+MIN_WIN, win.t2+dt));
        }
        setElements(prev => prev.map(el => el.id!==id ? el :
          { ...el, windows:(el.windows||[]).map(w => w.id===winId ? { ...w, t1, t2 } : w) }
        ));
        return;
      }

      // Door drag
      if (part==='door') {
        const { winId: doorId, winAction } = dragRef.current;
        const wdx = orig.x2-orig.x1, wdy = orig.y2-orig.y1;
        const wallLen2 = wdx*wdx + wdy*wdy;
        if (wallLen2 === 0) return;
        const dt = (dx*wdx + dy*wdy) / wallLen2;
        const door = (orig.doors||[]).find(d => d.id===doorId);
        if (!door) return;
        const MIN_DOOR = 20 / Math.sqrt(wallLen2);
        let t1 = door.t1, t2 = door.t2;
        if (winAction==='mv') {
          const span = door.t2-door.t1;
          t1 = Math.max(0, Math.min(1-span, door.t1+dt));
          t2 = t1+span;
        } else if (winAction==='t1') {
          t1 = Math.max(0, Math.min(door.t2-MIN_DOOR, door.t1+dt));
        } else if (winAction==='t2') {
          t2 = Math.min(1, Math.max(door.t1+MIN_DOOR, door.t2+dt));
        }
        setElements(prev => prev.map(el => el.id!==id ? el :
          { ...el, doors:(el.doors||[]).map(d => d.id===doorId ? { ...d, t1, t2 } : d) }
        ));
        return;
      }

      setElements(prev => prev.map(el => {
        if (el.id!==id) return el;
        if (el.type==='wall') {
          if (part==='move') return { ...el, x1:snap(orig.x1+dx), y1:snap(orig.y1+dy), x2:snap(orig.x2+dx), y2:snap(orig.y2+dy) };
          if (part==='p1')   return { ...el, x1:snap(orig.x1+dx), y1:snap(orig.y1+dy) };
          if (part==='p2')   return { ...el, x2:snap(orig.x2+dx), y2:snap(orig.y2+dy) };
        } else {
          if (part==='move')   return { ...el, x:snap(orig.x+dx), y:snap(orig.y+dy) };
          if (part==='resize') return { ...el, w:snap(Math.max(MIN_ELEM,orig.w+dx)), h:snap(Math.max(MIN_ELEM,orig.h+dy)) };
        }
        return el;
      }));
    } else if (kind==='toiletdoor') {
      const { id, doorId, side, action, orig } = dragRef.current;
      const sides = {
        top:    { x1:orig.x,        y1:orig.y,        x2:orig.x+orig.w, y2:orig.y        },
        right:  { x1:orig.x+orig.w, y1:orig.y,        x2:orig.x+orig.w, y2:orig.y+orig.h },
        bottom: { x1:orig.x+orig.w, y1:orig.y+orig.h, x2:orig.x,        y2:orig.y+orig.h },
        left:   { x1:orig.x,        y1:orig.y+orig.h, x2:orig.x,        y2:orig.y        },
      };
      const s = sides[side];
      const sdx=s.x2-s.x1, sdy=s.y2-s.y1, sLen2=sdx*sdx+sdy*sdy;
      if (sLen2===0) return;
      const dt=(dx*sdx+dy*sdy)/sLen2;
      const door=(orig.doors||[]).find(d=>d.id===doorId);
      if (!door) return;
      const MIN_D=20/Math.sqrt(sLen2);
      let t1=door.t1, t2=door.t2;
      if (action==='mv') { const span=t2-t1; t1=Math.max(0,Math.min(1-span,t1+dt)); t2=t1+span; }
      else if (action==='t1') t1=Math.max(0,Math.min(t2-MIN_D,t1+dt));
      else if (action==='t2') t2=Math.min(1,Math.max(t1+MIN_D,t2+dt));
      setElements(prev=>prev.map(el=>el.id!==id?el:
        {...el,doors:(el.doors||[]).map(d=>d.id===doorId?{...d,t1,t2}:d)}
      ));
    }
  }, []); // eslint-disable-line

  const onPointerUp = useCallback(() => {
    if (!dragRef.current) return;
    const dr = dragRef.current;
    dragRef.current = null;
    if (dr.kind==='table') savePositions(tablesRef.current.filter(t=>!!t.floor_plan_placed));
    if (dr.kind==='element'||dr.kind==='toiletdoor') {
      if (dr.part==='p1'||dr.part==='p2') {
        const snapped = snapWallEndpoint(elementsRef.current, dr.id, dr.part);
        if (snapped) setElements(snapped);
        saveElements(snapped||elementsRef.current);
      } else {
        saveElements(elementsRef.current);
      }
    }
  }, [savePositions, saveElements]);

  // ── panel field edits ────────────────────────────────────────────────────────
  const applyPanelField = async (field, value) => {
    const tbl = tables.find(t => t.id===editSel);
    if (!tbl) return;
    const update = { [field]: field==='capacity' ? parseInt(value) : value };
    await api.updateTable(tbl.id, update);
    setTables(prev => prev.map(t => t.id===editSel ? { ...t, ...update } : t));
  };

  const toggleTableShape = async (tbl) => {
    const newShape = (tbl.shape||'rect')==='rect' ? 'round' : 'rect';
    await api.updateTable(tbl.id, { shape: newShape });
    setTables(prev => prev.map(t => t.id===tbl.id ? { ...t, shape: newShape } : t));
  };

  // ── view modal ───────────────────────────────────────────────────────────────
  const openTableView = useCallback(async (tbl) => {
    setViewTable(tbl); setBooking(null); setBookingSuccess(''); setLoadResos(true);
    try {
      const resos = await api.getReservations({ table_id: tbl.id });
      setTableResos(resos.filter(r=>r.date>=today&&r.status!=='cancelled')
        .sort((a,b)=>a.date.localeCompare(b.date)||a.time_slot.localeCompare(b.time_slot)));
    } catch(_) { setTableResos([]); } finally { setLoadResos(false); }
  }, [today]);

  const openBookingForm = () => {
    setBooking({ date:today, time_slot:'', party_size:viewTable.capacity, customer_name:'', phone:'', email:'', special_requests:'', status:'confirmed' });
    setBookingTables([]); setBookingError(''); setBookingSuccess('');
  };

  useEffect(() => {
    if (!booking||!booking.date||!booking.time_slot||!booking.party_size) { setBookingTables([]); return; }
    let dead = false;
    setBookingChecking(true);
    api.getAvailability(booking.date, booking.time_slot, parseInt(booking.party_size))
      .then(d => { if (!dead) setBookingTables(d.tables||[]); })
      .catch(()  => { if (!dead) setBookingTables([]); })
      .finally(()=> { if (!dead) setBookingChecking(false); });
    return () => { dead=true; };
  }, [booking?.date, booking?.time_slot, booking?.party_size]); // eslint-disable-line

  const setB = (k,v) => setBooking(prev => ({ ...prev, [k]:v }));

  const submitBooking = async () => {
    setBookingError('');
    const miss = ['date','time_slot','party_size','customer_name'].filter(k=>!booking[k]);
    if (!booking.phone&&!booking.email) miss.push('phone or email');
    if (miss.length) { setBookingError('Please fill in: '+miss.join(', ')); return; }
    const tableId = bookingTables.find(t=>t.id===viewTable.id)?.id || viewTable.id;
    setBookingSaving(true);
    try {
      const res = await api.createReservation({ ...booking, table_id:tableId, party_size:parseInt(booking.party_size) });
      setBookingSuccess(`Booked! Confirmation: ${res.confirmation_number}`);
      setBooking(null); loadResoStatus();
      const resos = await api.getReservations({ table_id: viewTable.id });
      setTableResos(resos.filter(r=>r.date>=today&&r.status!=='cancelled')
        .sort((a,b)=>a.date.localeCompare(b.date)||a.time_slot.localeCompare(b.time_slot)));
    } catch(err) { setBookingError(err.message); } finally { setBookingSaving(false); }
  };

  // ── add / remove tables ───────────────────────────────────────────────────────
  const handleAddTableClick = () => {
    const unplaced = tables.filter(t=>!t.floor_plan_placed);
    setShowAdd(unplaced.length>0 ? 'unplaced' : 'new');
  };

  const placeTable = async (tbl) => {
    const placed = tablesRef.current.filter(t=>!!t.floor_plan_placed);
    const w = tbl.w||80, h = tbl.h||80;
    const { x, y } = findFreePosition(placed, elementsRef.current, w, h);
    const updated = { ...tbl, x, y, w, h, floor_plan_placed:1 };
    // Update local state immediately — no DB reload so stale floor_plan_placed values don't leak in
    setTables(prev=>prev.map(t=>t.id===tbl.id?updated:t));
    // Two separate calls: tables/:id owns floor_plan_placed; floorplan owns x/y/w/h
    await Promise.all([
      api.updateTable(tbl.id, { floor_plan_placed:1 }),
      api.saveFloorPlan([{ id:tbl.id, x, y, w, h }]),
    ]);
    const remaining = tablesRef.current.filter(t=>t.id!==tbl.id&&!t.floor_plan_placed);
    if (remaining.length===0) setShowAdd(false);
  };

  const addTable = async () => {
    if (!newTable.label) return;
    const result = await api.createTable({ ...newTable, capacity:parseInt(newTable.capacity) });
    const placed = tablesRef.current.filter(t=>!!t.floor_plan_placed);
    const { x, y } = findFreePosition(placed, elementsRef.current);
    const full = { ...result, x, y, w:80, h:80, floor_plan_placed:1, shape:'rect' };
    setTables(prev=>[...prev, full]);
    setNewTable({ label:'', capacity:4, location:'indoor' }); setShowAdd(false);
    await Promise.all([
      api.updateTable(result.id, { floor_plan_placed:1 }),
      api.saveFloorPlan([{ id:result.id, x, y, w:80, h:80 }]),
    ]);
  };

  const removeTable = (id) => {
    setConfirm({
      message: 'Remove from floor plan?',
      detail: 'The table stays in your Tables list and can be placed again anytime.',
      onConfirm: async () => {
        setConfirm(null);
        await api.updateTable(id, { floor_plan_placed: 0 });
        setTables(prev => prev.map(t => t.id === id ? { ...t, floor_plan_placed: 0 } : t));
        setEditSel(null);
      },
    });
  };

  const toggleActive = async (tbl) => {
    await api.updateTable(tbl.id, { is_active: tbl.is_active===1 ? 0 : 1 });
    setTables(prev=>prev.map(t=>t.id===tbl.id?{ ...t, is_active:tbl.is_active===1?0:1 }:t));
  };

  // ── add / remove elements ────────────────────────────────────────────────────
  const addElement = (type) => {
    let extra = {};
    if (type === 'door') {
      // Place door at midpoint of the wall whose midpoint is closest to canvas centre
      const walls = elementsRef.current.filter(e => e.type === 'wall');
      if (walls.length) {
        const cx = CANVAS_W / 2, cy = CANVAS_H / 2;
        const best = walls.reduce((b, w) => {
          const d = Math.hypot((w.x1+w.x2)/2 - cx, (w.y1+w.y2)/2 - cy);
          return d < b.d ? { w, d } : b;
        }, { w: walls[0], d: Infinity }).w;
        const mx = snap((best.x1 + best.x2) / 2 - DOOR_SIZE / 2);
        const my = snap((best.y1 + best.y2) / 2 - DOOR_SIZE / 2);
        // Rotation: align door hinge with wall direction (0=vertical wall, 90=horizontal wall)
        const angle = Math.atan2(best.y2 - best.y1, best.x2 - best.x1) * 180 / Math.PI;
        const rotation = ((90 - Math.round(angle / 90) * 90) + 360) % 360;
        extra = { x: mx, y: my, rotation };
      }
    }
    const defaults = {
      wall:   { x1: snap(CANVAS_W/2 - 90), y1: snap(CANVAS_H/2), x2: snap(CANVAS_W/2 + 90), y2: snap(CANVAS_H/2) },
      box:    { x: snap(CANVAS_W/2 - 70), y: snap(CANVAS_H/2 - 40), w:140, h:80, label:'Bar' },
      door:   { x: snap(CANVAS_W/2 - DOOR_SIZE/2), y: snap(CANVAS_H/2 - DOOR_SIZE/2), rotation:0, ...extra },
      toilet: { x: snap(CANVAS_W/2 - 50), y: snap(CANVAS_H/2 - 60), w:100, h:120, doors:[] },
      stairs: { x: snap(CANVAS_W/2 - 40), y: snap(CANVAS_H/2 - 60), w:80, h:120, rotation:0 },
    };
    const elem = { id:uid(), type, ...defaults[type] };
    const newElems = [...elementsRef.current, elem];
    setElements(newElems); setEditSelElem(elem.id); setEditSel(null);
    saveElements(newElems);
  };

  const removeElement = (id) => {
    const newElems = elementsRef.current.filter(e=>e.id!==id);
    setElements(newElems); setEditSelElem(null);
    saveElements(newElems);
  };

  // ── render element ───────────────────────────────────────────────────────────
  const renderElement = (elem) => {
    const sel = editMode && editSelElem===elem.id;

    if (elem.type==='wall') return null; // walls rendered in SVG overlay

    if (elem.type==='box') {
      return (
        <div key={elem.id} style={{
          position:'absolute', left:elem.x, top:elem.y, width:elem.w, height:elem.h,
          background: sel ? '#1e293b' : '#334155',
          border: sel ? '2px solid #4f46e5' : '2px solid #1e293b',
          borderRadius:8, display:'flex', alignItems:'center', justifyContent:'center',
          cursor: editMode ? 'grab' : 'default', zIndex:2, userSelect:'none', touchAction:'none',
          boxShadow: sel ? '0 0 0 2px #818cf8' : '0 2px 8px rgba(15,23,42,0.15)',
        }}
          onClick={e=>{ e.stopPropagation(); if (editMode) setEditSelElem(elem.id); }}
          onPointerDown={editMode ? (e=>onElemPointerDown(e,elem.id,'move')) : undefined}
        >
          <span style={{ color:'rgba(255,255,255,0.9)', fontSize:11, fontWeight:700, letterSpacing:'0.06em', textTransform:'uppercase', padding:'0 8px', textAlign:'center' }}>
            {elem.label||'Box'}
          </span>
          {editMode && (
            <div style={s.resizeHandle} onPointerDown={e=>{e.stopPropagation();onElemPointerDown(e,elem.id,'resize');}} />
          )}
        </div>
      );
    }

    if (elem.type==='door') {
      const rot = elem.rotation||0;
      return (
        <div key={elem.id} style={{
          position:'absolute', left:elem.x, top:elem.y, width:DOOR_SIZE, height:DOOR_SIZE,
          cursor: editMode ? 'grab' : 'default', zIndex:2, userSelect:'none', touchAction:'none',
        }}
          onClick={e=>{ e.stopPropagation(); if (editMode) setEditSelElem(elem.id); }}
          onPointerDown={editMode ? (e=>onElemPointerDown(e,elem.id,'move')) : undefined}
        >
          <svg width={DOOR_SIZE} height={DOOR_SIZE} viewBox="0 0 54 54" style={{ transform:`rotate(${rot}deg)`, transformOrigin:'center', display:'block' }}>
            {sel && <rect x="1" y="1" width="52" height="52" rx="4" fill="none" stroke="#4f46e5" strokeWidth="1.5" strokeDasharray="4,3" />}
            {/* wall line */}
            <line x1="4" y1="50" x2="4" y2="4" stroke={sel?'#4f46e5':'#334155'} strokeWidth="4" strokeLinecap="round" />
            {/* door panel */}
            <line x1="4" y1="4" x2="50" y2="4" stroke={sel?'#4f46e5':'#475569'} strokeWidth="2.5" strokeLinecap="round" />
            {/* swing arc */}
            <path d="M4,50 A46,46 0 0,1 50,4" stroke={sel?'#818cf8':'#94a3b8'} strokeWidth="1.5" fill="none" strokeDasharray="5,4" />
          </svg>
        </div>
      );
    }

    if (elem.type==='stairs') {
      const stepH = Math.max(8, Math.round(elem.h / Math.max(3, Math.floor(elem.h/14))));
      const rot = elem.rotation||0;
      return (
        <div key={elem.id} style={{
          position:'absolute', left:elem.x, top:elem.y, width:elem.w, height:elem.h,
          background:`repeating-linear-gradient(to bottom, #e2e8f0 0px, #e2e8f0 1px, #f8fafc 1px, #f8fafc ${stepH}px)`,
          border: sel ? '2px solid #4f46e5' : '2px solid #cbd5e1',
          borderRadius:4, overflow:'hidden',
          transform:`rotate(${rot}deg)`, transformOrigin:'center',
          cursor: editMode ? 'grab' : 'default', zIndex:2, userSelect:'none', touchAction:'none',
          display:'flex', alignItems:'center', justifyContent:'center',
          boxShadow: sel ? '0 0 0 2px #818cf8' : 'none',
        }}
          onClick={e=>{ e.stopPropagation(); if (editMode) setEditSelElem(elem.id); }}
          onPointerDown={editMode ? (e=>onElemPointerDown(e,elem.id,'move')) : undefined}
        >
          <span style={{ fontSize:9, fontWeight:800, color:'#94a3b8', letterSpacing:'0.08em', textTransform:'uppercase', writingMode:'vertical-rl', transform:'rotate(180deg)' }}>STAIRS</span>
          {editMode && (
            <div style={s.resizeHandle} onPointerDown={e=>{e.stopPropagation();onElemPointerDown(e,elem.id,'resize');}} />
          )}
        </div>
      );
    }

    return null;
  };

  if (loading) return <div style={{ color:'#64748b', padding:40 }}>Loading floor plan…</div>;

  // ── canvas dimensions ────────────────────────────────────────────────────────
  const placed = tables.filter(t=>!!t.floor_plan_placed);
  let canvasW, canvasH, viewOffsetX = 0, viewOffsetY = 0;
  if (editMode) {
    canvasW = EDIT_CANVAS_W;
    canvasH = EDIT_CANVAS_H;
  } else {
    // Symmetric canvas centred on the blue dot (CX, CY).
    // Grow halfW/halfH until every entity has VIEW_PAD clearance in all 4 directions.
    const CX = CANVAS_W / 2, CY = CANVAS_H / 2;
    let halfW = CX, halfH = CY;
    const grow = (l, t, r, b) => {
      halfW = Math.max(halfW, r - CX + VIEW_PAD, CX - l + VIEW_PAD);
      halfH = Math.max(halfH, b - CY + VIEW_PAD, CY - t + VIEW_PAD);
    };
    placed.forEach(t => grow(t.x, t.y, t.x+t.w, t.y+t.h));
    elements.forEach(e => {
      if (e.type==='wall')      grow(Math.min(e.x1,e.x2), Math.min(e.y1,e.y2), Math.max(e.x1,e.x2), Math.max(e.y1,e.y2));
      else if (e.type==='door') grow(e.x, e.y, e.x+DOOR_SIZE, e.y+DOOR_SIZE);
      else                       grow(e.x, e.y, e.x+(e.w||80), e.y+(e.h||80));
    });
    halfW = Math.round(halfW); halfH = Math.round(halfH);
    canvasW = halfW * 2;
    canvasH = halfH * 2;
    // Shift all content so the dot lands exactly at canvasW/2, canvasH/2
    viewOffsetX = halfW - CX;
    viewOffsetY = halfH - CY;
  }
  // Keep refs in sync so zoom/scroll handlers can read current canvas size
  canvasWRef.current = canvasW;
  canvasHRef.current = canvasH;

  const editSelTable   = tables.find(t=>t.id===editSel);
  // Wall SVG renderer
  const renderWallSVG = (elem) => {
    const sel = editMode && editSelElem===elem.id;
    const dx=elem.x2-elem.x1, dy=elem.y2-elem.y1;
    const len=Math.max(1,Math.sqrt(dx*dx+dy*dy));
    // Compute door gaps from wall-embedded doors
    const gaps = (elem.doors||[]).map(d => [d.t1, d.t2]);
    // Also cut gaps where a toilet room door is flush against this wall
    const TDSNAP = 25;
    for (const toilet of elements.filter(e => e.type==='toilet')) {
      const ts = {
        top:    { x1:toilet.x,          y1:toilet.y,          x2:toilet.x+toilet.w, y2:toilet.y          },
        right:  { x1:toilet.x+toilet.w, y1:toilet.y,          x2:toilet.x+toilet.w, y2:toilet.y+toilet.h },
        bottom: { x1:toilet.x+toilet.w, y1:toilet.y+toilet.h, x2:toilet.x,          y2:toilet.y+toilet.h },
        left:   { x1:toilet.x,          y1:toilet.y+toilet.h, x2:toilet.x,          y2:toilet.y          },
      };
      for (const d of (toilet.doors||[])) {
        const s = ts[d.side||'bottom'];
        const sdx=s.x2-s.x1, sdy=s.y2-s.y1;
        const gx1=s.x1+d.t1*sdx, gy1=s.y1+d.t1*sdy;
        const gx2=s.x1+d.t2*sdx, gy2=s.y1+d.t2*sdy;
        const p1=projectOnSeg(gx1,gy1,elem.x1,elem.y1,elem.x2,elem.y2);
        const p2=projectOnSeg(gx2,gy2,elem.x1,elem.y1,elem.x2,elem.y2);
        if (p1.dist<TDSNAP && p2.dist<TDSNAP) {
          const t1=Math.min(p1.t,p2.t), t2=Math.max(p1.t,p2.t);
          if (t2-t1>0.01 && t1<0.99 && t2>0.01) gaps.push([Math.max(0,t1),Math.min(1,t2)]);
        }
      }
    }
    gaps.sort((a,b)=>a[0]-b[0]);
    const merged=[];
    for (const g of gaps) {
      if (merged.length&&g[0]<=merged[merged.length-1][1]) merged[merged.length-1][1]=Math.max(merged[merged.length-1][1],g[1]);
      else merged.push([...g]);
    }
    const intervals=[]; let prev=0;
    for (const [a,b] of merged) { if (a>prev) intervals.push([prev,a]); prev=b; }
    if (prev<1) intervals.push([prev,1]);
    const color=sel?'#4f46e5':'#334155';
    return (
      <g key={elem.id}>
        {sel && <line x1={elem.x1} y1={elem.y1} x2={elem.x2} y2={elem.y2}
          stroke="#818cf8" strokeWidth={10} strokeLinecap="round" style={{pointerEvents:'none',opacity:0.2}}/>}
        {/* Invisible wide hit area */}
        <line x1={elem.x1} y1={elem.y1} x2={elem.x2} y2={elem.y2}
          stroke="transparent" strokeWidth={18}
          style={{cursor:editMode?'grab':'default',pointerEvents:editMode?'all':'none'}}
          onClick={e=>{e.stopPropagation();if(editMode)setEditSelElem(elem.id);}}
          onPointerDown={editMode?(e=>onElemPointerDown(e,elem.id,'move')):undefined}/>
        {/* Visible segments */}
        {intervals.map(([t0,t1],i)=>(
          <line key={i} x1={elem.x1+t0*dx} y1={elem.y1+t0*dy} x2={elem.x1+t1*dx} y2={elem.y1+t1*dy}
            stroke={color} strokeWidth={4} strokeLinecap="round" style={{pointerEvents:'none'}}/>
        ))}
        {/* Windows */}
        {(elem.windows||[]).map(win => {
          const wx1=elem.x1+win.t1*dx, wy1=elem.y1+win.t1*dy;
          const wx2=elem.x1+win.t2*dx, wy2=elem.y1+win.t2*dy;
          // Perpendicular unit vector for tick marks
          const px=-dy/len*7, py=dx/len*7;
          return (
            <g key={win.id}>
              {/* Cyan window segment */}
              <line x1={wx1} y1={wy1} x2={wx2} y2={wy2}
                stroke="#38bdf8" strokeWidth={5} strokeLinecap="round" style={{pointerEvents:'none'}}/>
              {/* Glazing tick marks */}
              {[0.2,0.5,0.8].map(t=>{
                const tx=wx1+t*(wx2-wx1), ty=wy1+t*(wy2-wy1);
                return <line key={t} x1={tx-px} y1={ty-py} x2={tx+px} y2={ty+py}
                  stroke="#7dd3fc" strokeWidth={1.5} style={{pointerEvents:'none'}}/>;
              })}
              {/* Drag handles (only when wall selected) */}
              {sel && <>
                <line x1={wx1} y1={wy1} x2={wx2} y2={wy2}
                  stroke="transparent" strokeWidth={14}
                  style={{cursor:'grab',pointerEvents:'all'}}
                  onPointerDown={e=>{e.stopPropagation();onWinPointerDown(e,elem.id,win.id,'mv');}}/>
                <circle cx={wx1} cy={wy1} r={6} fill="#38bdf8" stroke="#fff" strokeWidth={2}
                  style={{cursor:'ew-resize',pointerEvents:'all'}}
                  onPointerDown={e=>{e.stopPropagation();onWinPointerDown(e,elem.id,win.id,'t1');}}/>
                <circle cx={wx2} cy={wy2} r={6} fill="#38bdf8" stroke="#fff" strokeWidth={2}
                  style={{cursor:'ew-resize',pointerEvents:'all'}}
                  onPointerDown={e=>{e.stopPropagation();onWinPointerDown(e,elem.id,win.id,'t2');}}/>
              </>}
            </g>
          );
        })}
        {/* Doors */}
        {(elem.doors||[]).map(d => {
          const wx1=elem.x1+d.t1*dx, wy1=elem.y1+d.t1*dy;
          const wx2=elem.x1+d.t2*dx, wy2=elem.y1+d.t2*dy;
          const doorLen=Math.sqrt((wx2-wx1)**2+(wy2-wy1)**2);
          if (doorLen < 1) return null;
          const nx=-dy/len, ny=dx/len;
          const flip=d.flip ? -1 : 1;
          const dc=sel?'#4f46e5':'#475569';
          const ac=sel?'#818cf8':'#94a3b8';
          const handles = sel && (
            <>
              <line x1={wx1} y1={wy1} x2={wx2} y2={wy2} stroke="transparent" strokeWidth={14}
                style={{cursor:'grab',pointerEvents:'all'}}
                onPointerDown={e=>{e.stopPropagation();onDoorPointerDown(e,elem.id,d.id,'mv');}}/>
              <circle cx={wx1} cy={wy1} r={6} fill="#f59e0b" stroke="#fff" strokeWidth={2}
                style={{cursor:'ew-resize',pointerEvents:'all'}}
                onPointerDown={e=>{e.stopPropagation();onDoorPointerDown(e,elem.id,d.id,'t1');}}/>
              <circle cx={wx2} cy={wy2} r={6} fill="#f59e0b" stroke="#fff" strokeWidth={2}
                style={{cursor:'ew-resize',pointerEvents:'all'}}
                onPointerDown={e=>{e.stopPropagation();onDoorPointerDown(e,elem.id,d.id,'t2');}}/>
            </>
          );
          if (d.kind==='double') {
            const wmx=(wx1+wx2)/2, wmy=(wy1+wy2)/2, hl=doorLen/2;
            const olx=wx1+flip*hl*nx, oly=wy1+flip*hl*ny;
            const orx=wx2+flip*hl*nx, ory=wy2+flip*hl*ny;
            const cL=(wmx-wx1)*(oly-wy1)-(wmy-wy1)*(olx-wx1);
            const cR=(wmx-wx2)*(ory-wy2)-(wmy-wy2)*(orx-wx2);
            return (
              <g key={d.id}>
                <line x1={wx1} y1={wy1} x2={olx} y2={oly} stroke={dc} strokeWidth={2} strokeLinecap="round" style={{pointerEvents:'none'}}/>
                <path d={`M${wmx},${wmy} A${hl},${hl} 0 0,${cL>0?1:0} ${olx},${oly}`} stroke={ac} strokeWidth={1.5} fill="none" strokeDasharray="4,3" style={{pointerEvents:'none'}}/>
                <line x1={wx2} y1={wy2} x2={orx} y2={ory} stroke={dc} strokeWidth={2} strokeLinecap="round" style={{pointerEvents:'none'}}/>
                <path d={`M${wmx},${wmy} A${hl},${hl} 0 0,${cR>0?1:0} ${orx},${ory}`} stroke={ac} strokeWidth={1.5} fill="none" strokeDasharray="4,3" style={{pointerEvents:'none'}}/>
                {handles}
              </g>
            );
          }
          // Single door — hinge at t1 end
          const opx=wx1+flip*doorLen*nx, opy=wy1+flip*doorLen*ny;
          const cross=(wx2-wx1)*(opy-wy1)-(wy2-wy1)*(opx-wx1);
          return (
            <g key={d.id}>
              <line x1={wx1} y1={wy1} x2={opx} y2={opy} stroke={dc} strokeWidth={2} strokeLinecap="round" style={{pointerEvents:'none'}}/>
              <path d={`M${wx2},${wy2} A${doorLen},${doorLen} 0 0,${cross>0?1:0} ${opx},${opy}`} stroke={ac} strokeWidth={1.5} fill="none" strokeDasharray="4,3" style={{pointerEvents:'none'}}/>
              {handles}
            </g>
          );
        })}
        {/* Endpoint handles */}
        {sel && <>
          <circle cx={elem.x1} cy={elem.y1} r={8} fill="#fff" stroke="#4f46e5" strokeWidth={2.5}
            style={{cursor:'crosshair',pointerEvents:'all'}}
            onPointerDown={e=>{e.stopPropagation();onElemPointerDown(e,elem.id,'p1');}}/>
          <circle cx={elem.x2} cy={elem.y2} r={8} fill="#fff" stroke="#4f46e5" strokeWidth={2.5}
            style={{cursor:'crosshair',pointerEvents:'all'}}
            onPointerDown={e=>{e.stopPropagation();onElemPointerDown(e,elem.id,'p2');}}/>
        </>}
      </g>
    );
  };
  // ── Toilet SVG renderer ───────────────────────────────────────────────────────
  const renderToiletSVG = (elem) => {
    const sel = editMode && editSelElem===elem.id;
    const { x, y, w, h } = elem;
    const wallSides = {
      top:    { x1:x,   y1:y,   x2:x+w, y2:y   },
      right:  { x1:x+w, y1:y,   x2:x+w, y2:y+h },
      bottom: { x1:x+w, y1:y+h, x2:x,   y2:y+h },
      left:   { x1:x,   y1:y+h, x2:x,   y2:y   },
    };
    const bc = sel ? '#4f46e5' : '#475569';
    return (
      <g key={elem.id}>
        {/* Fill */}
        <rect x={x} y={y} width={w} height={h} fill="rgba(241,245,249,0.85)" style={{pointerEvents:'none'}}/>
        {/* Hit area */}
        <rect x={x} y={y} width={w} height={h} fill="transparent"
          style={{cursor:editMode?'grab':'default', pointerEvents:editMode?'all':'none'}}
          onClick={e=>{e.stopPropagation();if(editMode)setEditSelElem(elem.id);}}
          onPointerDown={editMode?(e=>onElemPointerDown(e,elem.id,'move')):undefined}/>
        {/* Selection ring */}
        {sel && <rect x={x-2} y={y-2} width={w+4} height={h+4} rx={3} fill="none" stroke="#818cf8" strokeWidth={1.5} strokeDasharray="5,3" style={{pointerEvents:'none'}}/>}
        {/* WC label */}
        <text x={x+w*0.5} y={y+h*0.5+5} textAnchor="middle" fontSize={Math.max(10,Math.min(18,w*0.18))}
          fill="#94a3b8" fontFamily="system-ui,sans-serif" fontWeight="700" style={{pointerEvents:'none',userSelect:'none'}}>WC</text>
        {/* 4 sides with door gaps */}
        {['top','right','bottom','left'].map(side => {
          const s = wallSides[side];
          const sdx=s.x2-s.x1, sdy=s.y2-s.y1;
          const slen=Math.max(1,Math.sqrt(sdx*sdx+sdy*sdy));
          const nx=-sdy/slen, ny=sdx/slen;
          const doorList=(elem.doors||[]).filter(d=>d.side===side);
          const gaps=doorList.map(d=>[d.t1,d.t2]);
          gaps.sort((a,b)=>a[0]-b[0]);
          const merged=[]; for(const g of gaps){if(merged.length&&g[0]<=merged[merged.length-1][1])merged[merged.length-1][1]=Math.max(merged[merged.length-1][1],g[1]);else merged.push([...g]);}
          const intervals=[]; let prev=0; for(const[a,b]of merged){if(a>prev)intervals.push([prev,a]);prev=b;} if(prev<1)intervals.push([prev,1]);
          return (
            <g key={side}>
              {intervals.map(([t0,t1],i)=>(
                <line key={i} x1={s.x1+t0*sdx} y1={s.y1+t0*sdy} x2={s.x1+t1*sdx} y2={s.y1+t1*sdy}
                  stroke={bc} strokeWidth={3} strokeLinecap="round" style={{pointerEvents:'none'}}/>
              ))}
              {doorList.map(d=>{
                const wx1=s.x1+d.t1*sdx, wy1=s.y1+d.t1*sdy;
                const wx2=s.x1+d.t2*sdx, wy2=s.y1+d.t2*sdy;
                const dl=Math.sqrt((wx2-wx1)**2+(wy2-wy1)**2); if(dl<1)return null;
                const flip=d.flip?-1:1;
                const dc=sel?'#4f46e5':'#475569', ac=sel?'#818cf8':'#94a3b8';
                const handles=sel&&(
                  <>
                    <line x1={wx1} y1={wy1} x2={wx2} y2={wy2} stroke="transparent" strokeWidth={14}
                      style={{cursor:'grab',pointerEvents:'all'}}
                      onPointerDown={e=>{e.stopPropagation();onToiletDoorPointerDown(e,elem.id,d.id,side,'mv');}}/>
                    <circle cx={wx1} cy={wy1} r={6} fill="#f59e0b" stroke="#fff" strokeWidth={2}
                      style={{cursor:'ew-resize',pointerEvents:'all'}}
                      onPointerDown={e=>{e.stopPropagation();onToiletDoorPointerDown(e,elem.id,d.id,side,'t1');}}/>
                    <circle cx={wx2} cy={wy2} r={6} fill="#f59e0b" stroke="#fff" strokeWidth={2}
                      style={{cursor:'ew-resize',pointerEvents:'all'}}
                      onPointerDown={e=>{e.stopPropagation();onToiletDoorPointerDown(e,elem.id,d.id,side,'t2');}}/>
                  </>
                );
                if(d.kind==='double'){
                  const wmx=(wx1+wx2)/2,wmy=(wy1+wy2)/2,hl=dl/2;
                  const olx=wx1+flip*hl*nx,oly=wy1+flip*hl*ny,orx=wx2+flip*hl*nx,ory=wy2+flip*hl*ny;
                  const cL=(wmx-wx1)*(oly-wy1)-(wmy-wy1)*(olx-wx1);
                  const cR=(wmx-wx2)*(ory-wy2)-(wmy-wy2)*(orx-wx2);
                  return(<g key={d.id}>
                    <line x1={wx1} y1={wy1} x2={olx} y2={oly} stroke={dc} strokeWidth={2} strokeLinecap="round" style={{pointerEvents:'none'}}/>
                    <path d={`M${wmx},${wmy} A${hl},${hl} 0 0,${cL>0?1:0} ${olx},${oly}`} stroke={ac} strokeWidth={1.5} fill="none" strokeDasharray="4,3" style={{pointerEvents:'none'}}/>
                    <line x1={wx2} y1={wy2} x2={orx} y2={ory} stroke={dc} strokeWidth={2} strokeLinecap="round" style={{pointerEvents:'none'}}/>
                    <path d={`M${wmx},${wmy} A${hl},${hl} 0 0,${cR>0?1:0} ${orx},${ory}`} stroke={ac} strokeWidth={1.5} fill="none" strokeDasharray="4,3" style={{pointerEvents:'none'}}/>
                    {handles}
                  </g>);
                }
                const opx=wx1+flip*dl*nx,opy=wy1+flip*dl*ny;
                const cross=(wx2-wx1)*(opy-wy1)-(wy2-wy1)*(opx-wx1);
                return(<g key={d.id}>
                  <line x1={wx1} y1={wy1} x2={opx} y2={opy} stroke={dc} strokeWidth={2} strokeLinecap="round" style={{pointerEvents:'none'}}/>
                  <path d={`M${wx2},${wy2} A${dl},${dl} 0 0,${cross>0?1:0} ${opx},${opy}`} stroke={ac} strokeWidth={1.5} fill="none" strokeDasharray="4,3" style={{pointerEvents:'none'}}/>
                  {handles}
                </g>);
              })}
            </g>
          );
        })}
        {/* Resize handle */}
        {sel && <circle cx={x+w} cy={y+h} r={7} fill="#4f46e5" stroke="#fff" strokeWidth={2}
          style={{cursor:'se-resize',pointerEvents:editMode?'all':'none'}}
          onPointerDown={e=>{e.stopPropagation();onElemPointerDown(e,elem.id,'resize');}}/>}
      </g>
    );
  };

  const editSelElemData= elements.find(e=>e.id===editSelElem);
  const placedTables   = tables.filter(t=>!!t.floor_plan_placed);
  const areas          = [...new Set(placedTables.map(t=>t.location))].filter(Boolean).sort();
  const visibleTables  = activeArea ? placedTables.filter(t=>t.location===activeArea) : placedTables;
  const windowSeatIds  = getWindowSeatIds(placedTables, elements);

  return (
    <div>
      {/* Header */}
      <div style={s.header}>
        <div>
          <h1 style={s.h1}>Floor Plan</h1>
          <p style={s.sub}>
            {editMode
              ? 'Drag tables & elements · Pull ↘ corner to resize · Click to select'
              : 'Click a table to see upcoming reservations or add a booking'}
          </p>
        </div>
        <div style={{ display:'flex', gap:8, alignItems:'center' }}>
          {saving && <span style={s.savingText}>Saving…</span>}
          {saved  && <span style={s.savedText}>Saved ✓</span>}
          {/* Zoom controls */}
          <div style={s.zoomGroup}>
            <button style={s.zoomBtn} onClick={()=>{
              // In view mode: don't allow zooming out past the level where all content fits
              const minZ = !editModeRef.current && wrapperRef.current
                ? +Math.max(0.1, Math.min(
                    wrapperRef.current.clientWidth  / canvasWRef.current,
                    wrapperRef.current.clientHeight / canvasHRef.current
                  )).toFixed(2)
                : 0.25;
              handleZoom(Math.max(minZ, +(zoom-0.25).toFixed(2)));
            }} title="Zoom out">−</button>
            <span style={s.zoomLabel}>{Math.round(zoom*100)}%</span>
            <button style={s.zoomBtn} onClick={()=>handleZoom(Math.min(3,+(zoom+0.25).toFixed(2)))} title="Zoom in">+</button>
            {zoom!==1 && <button style={s.zoomReset} onClick={()=>handleZoom(1)}>Reset</button>}
          </div>
          {editMode && (
            <button style={{ ...s.addBtn, position:'relative' }} onClick={handleAddTableClick}>
              + Add Table
              {tables.filter(t=>!t.floor_plan_placed).length>0 && (
                <span style={s.badge}>!</span>
              )}
            </button>
          )}
          <button style={{ ...s.editBtn, ...(editMode?s.editBtnActive:{}), position:'relative' }} onClick={()=>setEditMode(e=>!e)}>
            {editMode ? 'Done editing' : 'Edit layout'}
            {!editMode && tables.filter(t=>!t.floor_plan_placed).length > 0 && (
              <span style={{ ...s.badge, top:-6, right:-6, background:'#f59e0b' }}>!</span>
            )}
          </button>
        </div>
      </div>

      {/* Element toolbar (edit mode only) */}
      {editMode && (
        <div style={s.elemToolbar}>
          <span style={s.elemToolbarLabel}>Add:</span>
          {[['wall','Wall'],['box','Box'],['toilet','Toilet'],['stairs','Stairs']].map(([type,label])=>(
            <button key={type} style={s.elemToolBtn} onClick={()=>addElement(type)}>{label}</button>
          ))}
          <span style={s.elemToolbarHint}>Wall: drag endpoints to reshape · Select a wall or toilet to add doors &amp; windows</span>
        </div>
      )}

      {/* Completeness banner — view mode only */}
      {!editMode && (() => {
        const unplaced     = tables.filter(t => !t.floor_plan_placed);
        const connectedW   = largestConnectedWalls(elements);
        const roomComplete = connectedW >= 3;   // needs ≥3 connected walls to form a room
        const hasAnyWall   = elements.some(e => e.type === 'wall');
        if (unplaced.length > 0) return (
          <div style={{ ...s.banner, ...s.bannerWarn }}>
            <span>⚠️ <strong>{unplaced.length} table{unplaced.length>1?'s':''}</strong> not on the floor plan yet</span>
            <button style={s.bannerBtn} onClick={handleAddTableClick}>Place now →</button>
          </div>
        );
        if (tables.length > 0 && !hasAnyWall) return (
          <div style={{ ...s.banner, ...s.bannerInfo }}>
            <span>ℹ️ No walls drawn — add walls so the AI understands your layout</span>
            <button style={s.bannerBtn} onClick={() => setEditMode(true)}>Edit layout →</button>
          </div>
        );
        if (tables.length > 0 && hasAnyWall && !roomComplete) return (
          <div style={{ ...s.banner, ...s.bannerInfo }}>
            <span>ℹ️ Walls don't form a room yet — connect at least 3 wall segments endpoint-to-endpoint</span>
            <button style={s.bannerBtn} onClick={() => setEditMode(true)}>Edit layout →</button>
          </div>
        );
        if (tables.length > 0 && roomComplete && !aiEnabled) return (
          <div style={{ ...s.banner, ...s.bannerInfo }}>
            <span>✅ Floor plan looks good — turn on <strong>Floor plan AI mode</strong> in Settings for the AI to use it</span>
          </div>
        );
        if (tables.length > 0 && roomComplete && aiEnabled) return (
          <div style={{ ...s.banner, ...s.bannerOk }}>
            <span>✅ Floor plan active — the AI is using it for bookings. Keep it accurate: an outdated or illogical layout can affect booking quality.</span>
          </div>
        );
        return null;
      })()}

      {/* Area tabs */}
      {areas.length>1 && (
        <div style={s.areaTabs}>
          <button style={{ ...s.areaTab, ...(activeArea===null?s.areaTabActive:{}) }} onClick={()=>setActiveArea(null)}>All areas</button>
          {areas.map(area=>(
            <button key={area} style={{ ...s.areaTab, ...(activeArea===area?s.areaTabActive:{}), ...(activeArea===area?{ borderColor:LOC_COLOR[area]||'#4f46e5', color:LOC_COLOR[area]||'#4f46e5' }:{}) }}
              onClick={()=>setActiveArea(activeArea===area?null:area)}>
              <span style={{ width:8, height:8, borderRadius:'50%', background:LOC_COLOR[area]||'#94a3b8', display:'inline-block', marginRight:6, flexShrink:0 }} />
              {area.charAt(0).toUpperCase()+area.slice(1)}
            </button>
          ))}
        </div>
      )}

      <div style={s.layout}>
        {/* Canvas wrapper (scrollable) */}
        <div style={s.canvasWrapper} ref={wrapperRef}>
          {/* margin:auto centers when zoomed out; scroll works when zoomed in */}
          <div style={{ width: canvasW * zoom, height: canvasH * zoom, flexShrink: 0, margin: 'auto', position: 'relative' }}>
          <div
            style={{ ...s.canvas, width:canvasW, height:canvasH, transform:`scale(${zoom})`, transformOrigin:'top left', position:'absolute', top:0, left:0 }}
            onClick={()=>{ if (editMode) { setEditSel(null); setEditSelElem(null); } }}
            onPointerMove={editMode ? onPointerMove : undefined}
            onPointerUp={editMode ? onPointerUp : undefined}
          >
            <div style={s.gridBg} />

            {/* Offset container — centres content on dot in both edit and view mode */}
            <div style={{ position:'absolute', left: editMode ? EDIT_OFFSET_X : viewOffsetX, top: editMode ? EDIT_OFFSET_Y : viewOffsetY }}>

            {/* Center marker dot */}
            <div style={{ position:'absolute', left:CANVAS_W/2-5, top:CANVAS_H/2-5, width:10, height:10, borderRadius:'50%', background:'#c7d2fe', pointerEvents:'none', zIndex:0 }} />

            {/* Wall / toilet SVG overlay — walls first so toilet handles paint on top */}
            <svg style={{ position:'absolute', left:0, top:0, width: CANVAS_W+200, height: CANVAS_H+200, overflow:'visible', zIndex:2, pointerEvents:'none' }}>
              {elements.filter(e=>e.type==='wall').map(renderWallSVG)}
              {elements.filter(e=>e.type==='toilet').map(renderToiletSVG)}
            </svg>

            {/* Decorative elements (box, door, stairs) */}
            {elements.filter(e=>e.type!=='wall'&&e.type!=='toilet').map(renderElement)}

            {/* Tables */}
            {visibleTables.map(tbl => {
              const color    = LOC_COLOR[tbl.location]||'#4f46e5';
              const inactive = tbl.is_active===0;
              const isEditSel= editMode && editSel===tbl.id;
              const rStat    = resoStatus[tbl.id];
              const statusColor = rStat==='today'?'#10b981':rStat==='upcoming'?'#f59e0b':null;
              const round    = (tbl.shape||'rect')==='round';
              const isWindow = windowSeatIds.has(tbl.id);
              return (
                <div key={tbl.id} style={{
                  ...s.table,
                  left:tbl.x, top:tbl.y, width:tbl.w, height:tbl.h,
                  borderRadius: round ? '50%' : 12,
                  borderColor: isEditSel?color:inactive?'#cbd5e1':'#e2e8f0',
                  background:  isEditSel?`${color}18`:inactive?'#f8fafc':'#fff',
                  boxShadow:   isEditSel?`0 0 0 2px ${color}`:'0 1px 6px rgba(15,23,42,0.08)',
                  cursor: editMode?'grab':'pointer',
                  opacity: inactive?0.55:1,
                }}
                  onClick={e=>{ e.stopPropagation(); if (editMode) setEditSel(tbl.id); else openTableView(tbl); }}
                  onPointerDown={editMode?(e=>onPointerDown(e,tbl.id,'move')):undefined}
                >
                  <div style={{ ...s.tableLabel, color:inactive?'#94a3b8':color }}>{tbl.label}</div>
                  <div style={s.tableCap}>{tbl.capacity}p</div>
                  {!round && <div style={{ ...s.locDot, background:color }} />}
                  {isWindow && <div style={s.windowBadge} title="Window seat">🪟</div>}
                  {statusColor && <div style={{ ...s.resoIndicator, background:statusColor }} title={rStat==='today'?'Booked today':'Upcoming booking'} />}
                  {editMode && (
                    <div style={s.resizeHandle} onPointerDown={e=>{ e.stopPropagation(); onPointerDown(e,tbl.id,'resize'); }} />
                  )}
                </div>
              );
            })}
            {/* Measurement line: dot → selected entity centre (edit mode only) */}
            {editMode && (editSel || editSelElem) && (() => {
              const CX = CANVAS_W / 2, CY = CANVAS_H / 2;
              let ex, ey;
              if (editSel) {
                const t = tables.find(t => t.id === editSel);
                if (!t) return null;
                ex = t.x + t.w / 2; ey = t.y + t.h / 2;
              } else {
                const el = elements.find(e => e.id === editSelElem);
                if (!el) return null;
                if (el.type === 'wall')     { ex = (el.x1+el.x2)/2; ey = (el.y1+el.y2)/2; }
                else if (el.type === 'door') { ex = el.x+DOOR_SIZE/2; ey = el.y+DOOR_SIZE/2; }
                else                         { ex = el.x+(el.w||80)/2; ey = el.y+(el.h||80)/2; }
              }
              const dist = Math.round(Math.hypot(ex - CX, ey - CY));
              const mx = (CX + ex) / 2, my = (CY + ey) / 2;
              const labelW = dist >= 1000 ? 52 : dist >= 100 ? 44 : 38;
              return (
                <svg key="measure" style={{ position:'absolute', left:0, top:0, overflow:'visible', pointerEvents:'none', zIndex:20 }}>
                  <line x1={CX} y1={CY} x2={ex} y2={ey} stroke="#6366f1" strokeWidth={1.5} strokeDasharray="6,4" opacity={0.65} />
                  <circle cx={CX} cy={CY} r={3} fill="#6366f1" opacity={0.65} />
                  <circle cx={ex} cy={ey} r={3} fill="#6366f1" opacity={0.65} />
                  <rect x={mx - labelW/2} y={my - 11} width={labelW} height={20} rx={5} fill="#6366f1" />
                  <text x={mx} y={my + 4} textAnchor="middle" fill="#fff" fontSize={11} fontWeight="700" fontFamily="system-ui,sans-serif">{dist} px</text>
                </svg>
              );
            })()}

            </div>{/* end offset container */}
          </div>
          </div>{/* end scaling wrapper */}
        </div>

        {/* Side panel */}
        <div style={s.panel}>
          {editMode ? (
            editSelTable ? (
              <div style={s.panelInner}>
                <div style={s.panelTitle}>{editSelTable.label}</div>
                <div style={s.panelDivider} />
                <div style={s.panelField}>
                  <label style={s.panelFieldLabel}>Label</label>
                  <input style={s.panelInput} value={panelEdit.label||''} onChange={e=>setPanelEdit(p=>({...p,label:e.target.value}))} onBlur={e=>applyPanelField('label',e.target.value)} />
                </div>
                <div style={s.panelField}>
                  <label style={s.panelFieldLabel}>Capacity</label>
                  <input style={s.panelInput} type="number" min="1" value={panelEdit.capacity||''} onChange={e=>setPanelEdit(p=>({...p,capacity:e.target.value}))} onBlur={e=>applyPanelField('capacity',e.target.value)} />
                </div>
                <div style={s.panelField}>
                  <label style={s.panelFieldLabel}>Location</label>
                  <select style={s.panelInput} value={panelEdit.location||'indoor'} onChange={e=>{ setPanelEdit(p=>({...p,location:e.target.value})); applyPanelField('location',e.target.value); }}>
                    {LOCATIONS.map(l=><option key={l} value={l}>{l.charAt(0).toUpperCase()+l.slice(1)}</option>)}
                  </select>
                </div>
                <div style={s.panelField}>
                  <label style={s.panelFieldLabel}>Shape</label>
                  <button style={s.panelBtn} onClick={()=>toggleTableShape(editSelTable)}>
                    {(editSelTable.shape||'rect')==='rect' ? '◻ Switch to round' : '○ Switch to square'}
                  </button>
                </div>
                <div style={s.panelDivider} />
                <div style={s.panelRow}>
                  <span style={s.panelKey}>Status</span>
                  <span style={{ ...s.panelVal, color:editSelTable.is_active===1?'#10b981':'#94a3b8' }}>
                    {editSelTable.is_active===1?'Active':'Inactive'}
                  </span>
                </div>
                <div style={s.panelRow}>
                  <span style={s.panelKey}>Size</span>
                  <span style={s.panelVal}>{editSelTable.w}×{editSelTable.h}</span>
                </div>
                <div style={s.panelDivider} />
                <button style={s.panelBtn} onClick={()=>toggleActive(editSelTable)}>
                  {editSelTable.is_active===1?'Deactivate':'Activate'}
                </button>
                <button style={{ ...s.panelBtn, ...s.removePanelBtn }} onClick={()=>removeTable(editSelTable.id)}>Remove from floor plan</button>
              </div>
            ) : editSelElemData ? (
              <div style={s.panelInner}>
                <div style={s.panelTitle}>
                  {editSelElemData.type==='wall'?'Wall':editSelElemData.type==='box'?'Box':editSelElemData.type==='door'?'Door':editSelElemData.type==='toilet'?'Toilet Room':'Stairs'}
                </div>
                <div style={s.panelDivider} />
                {editSelElemData.type==='box' && (
                  <div style={s.panelField}>
                    <label style={s.panelFieldLabel}>Label</label>
                    <input style={s.panelInput} value={elemPanelEdit.label||''}
                      onChange={e=>setElemPanelEdit(p=>({...p,label:e.target.value}))}
                      onBlur={e=>{
                        const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,label:e.target.value}:el);
                        setElements(newElems); saveElements(newElems);
                      }}
                    />
                  </div>
                )}
                {editSelElemData.type==='wall' && (
                  <div>
                    <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 16px', lineHeight:1.5 }}>
                      Drag the blue dots at each end to reshape the wall.
                    </p>
                    <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>Windows</div>
                    {(editSelElemData.windows||[]).length === 0 && (
                      <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 10px' }}>No windows on this wall yet.</p>
                    )}
                    {(editSelElemData.windows||[]).map((win, i) => (
                      <div key={win.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#f0f9ff', border:'1px solid #bae6fd', borderRadius:8, marginBottom:6 }}>
                        <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                          <div style={{ width:20, height:4, background:'#38bdf8', borderRadius:2 }}/>
                          <span style={{ fontSize:12, color:'#0369a1', fontWeight:600 }}>Window {i+1}</span>
                        </div>
                        <button style={{ fontSize:11, color:'#ef4444', background:'none', border:'none', cursor:'pointer', padding:'2px 4px', lineHeight:1 }}
                          onClick={()=>{
                            const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,windows:(el.windows||[]).filter(w=>w.id!==win.id)}:el);
                            setElements(newElems); saveElements(newElems);
                          }}>✕</button>
                      </div>
                    ))}
                    <button style={s.panelBtn} onClick={()=>{
                      const newWin={ id:uid(), t1:0.3, t2:0.7 };
                      const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,windows:[...(el.windows||[]),newWin]}:el);
                      setElements(newElems); saveElements(newElems);
                    }}>+ Add window</button>
                    <div style={{ marginTop:14 }}>
                      <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>Doors</div>
                      {(editSelElemData.doors||[]).length === 0 && (
                        <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 10px' }}>No doors on this wall yet.</p>
                      )}
                      {(editSelElemData.doors||[]).map((door, i) => (
                        <div key={door.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, marginBottom:6 }}>
                          <div style={{ display:'flex', alignItems:'center', gap:7 }}>
                            <div style={{ width:20, height:4, background:'#f59e0b', borderRadius:2 }}/>
                            <span style={{ fontSize:12, color:'#92400e', fontWeight:600 }}>{door.kind==='double'?'Double':'Single'} Door {i+1}</span>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <button style={{ fontSize:11, color:'#6366f1', background:'none', border:'1px solid #e0e7ff', borderRadius:4, cursor:'pointer', padding:'2px 6px', lineHeight:1.4 }}
                              onClick={()=>{
                                const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,doors:(el.doors||[]).map(d=>d.id===door.id?{...d,flip:!d.flip}:d)}:el);
                                setElements(newElems); saveElements(newElems);
                              }}>Flip</button>
                            <button style={{ fontSize:11, color:'#ef4444', background:'none', border:'none', cursor:'pointer', padding:'2px 4px', lineHeight:1 }}
                              onClick={()=>{
                                const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,doors:(el.doors||[]).filter(d=>d.id!==door.id)}:el);
                                setElements(newElems); saveElements(newElems);
                              }}>✕</button>
                          </div>
                        </div>
                      ))}
                      <div style={{ display:'flex', gap:6 }}>
                        <button style={{...s.panelBtn, flex:1}} onClick={()=>{
                          const newDoor={ id:uid(), t1:0.3, t2:0.6, kind:'single', flip:false };
                          const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,doors:[...(el.doors||[]),newDoor]}:el);
                          setElements(newElems); saveElements(newElems);
                        }}>+ Single</button>
                        <button style={{...s.panelBtn, flex:1}} onClick={()=>{
                          const newDoor={ id:uid(), t1:0.2, t2:0.8, kind:'double', flip:false };
                          const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,doors:[...(el.doors||[]),newDoor]}:el);
                          setElements(newElems); saveElements(newElems);
                        }}>+ Double</button>
                      </div>
                    </div>
                  </div>
                )}
                {editSelElemData.type==='door' && (
                  <div style={s.panelField}>
                    <label style={s.panelFieldLabel}>Opens toward</label>
                    <select style={s.panelInput} value={editSelElemData.rotation||0}
                      onChange={e=>{
                        const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,rotation:parseInt(e.target.value)}:el);
                        setElements(newElems); saveElements(newElems);
                      }}
                    >
                      <option value={0}>Right →</option>
                      <option value={90}>Down ↓</option>
                      <option value={180}>Left ←</option>
                      <option value={270}>Up ↑</option>
                    </select>
                  </div>
                )}
                {editSelElemData.type==='toilet' && (() => {
                  const SIDES = ['top','right','bottom','left'];
                  return (
                    <div>
                      <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 14px', lineHeight:1.5 }}>
                        Drag to reposition. Pull the corner handle to resize.
                      </p>
                      <div style={{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.07em', marginBottom:8 }}>Doors</div>
                      {(editSelElemData.doors||[]).length === 0 && (
                        <p style={{ fontSize:12, color:'#94a3b8', margin:'0 0 10px' }}>No doors yet.</p>
                      )}
                      {(editSelElemData.doors||[]).map((door, i) => (
                        <div key={door.id} style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'7px 10px', background:'#fffbeb', border:'1px solid #fde68a', borderRadius:8, marginBottom:6 }}>
                          <div style={{ display:'flex', flexDirection:'column', gap:4 }}>
                            <span style={{ fontSize:12, color:'#92400e', fontWeight:600 }}>{door.kind==='double'?'Double':'Single'} Door {i+1}</span>
                            <select style={{ fontSize:11, border:'1px solid #fde68a', borderRadius:4, padding:'1px 4px', background:'#fffbeb', color:'#92400e' }}
                              value={door.side||'bottom'}
                              onChange={e=>{
                                const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,doors:(el.doors||[]).map(d=>d.id===door.id?{...d,side:e.target.value}:d)}:el);
                                setElements(newElems); saveElements(newElems);
                              }}>
                              {SIDES.map(s=><option key={s} value={s}>{s.charAt(0).toUpperCase()+s.slice(1)}</option>)}
                            </select>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:6 }}>
                            <button style={{ fontSize:11, color:'#6366f1', background:'none', border:'1px solid #e0e7ff', borderRadius:4, cursor:'pointer', padding:'2px 6px', lineHeight:1.4 }}
                              onClick={()=>{
                                const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,doors:(el.doors||[]).map(d=>d.id===door.id?{...d,flip:!d.flip}:d)}:el);
                                setElements(newElems); saveElements(newElems);
                              }}>Flip</button>
                            <button style={{ fontSize:11, color:'#ef4444', background:'none', border:'none', cursor:'pointer', padding:'2px 4px', lineHeight:1 }}
                              onClick={()=>{
                                const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,doors:(el.doors||[]).filter(d=>d.id!==door.id)}:el);
                                setElements(newElems); saveElements(newElems);
                              }}>✕</button>
                          </div>
                        </div>
                      ))}
                      <div style={{ display:'flex', gap:6, marginTop:4 }}>
                        <button style={{...s.panelBtn, flex:1}} onClick={()=>{
                          const newDoor={ id:uid(), t1:0.3, t2:0.6, kind:'single', side:'bottom', flip:false };
                          const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,doors:[...(el.doors||[]),newDoor]}:el);
                          setElements(newElems); saveElements(newElems);
                        }}>+ Single</button>
                        <button style={{...s.panelBtn, flex:1}} onClick={()=>{
                          const newDoor={ id:uid(), t1:0.2, t2:0.8, kind:'double', side:'bottom', flip:false };
                          const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,doors:[...(el.doors||[]),newDoor]}:el);
                          setElements(newElems); saveElements(newElems);
                        }}>+ Double</button>
                      </div>
                    </div>
                  );
                })()}
                {editSelElemData.type==='stairs' && (
                  <div style={s.panelField}>
                    <label style={s.panelFieldLabel}>Rotation</label>
                    <select style={s.panelInput} value={editSelElemData.rotation||0}
                      onChange={e=>{
                        const newElems=elementsRef.current.map(el=>el.id===editSelElem?{...el,rotation:parseInt(e.target.value)}:el);
                        setElements(newElems); saveElements(newElems);
                      }}
                    >
                      <option value={0}>0° — steps go up ↑</option>
                      <option value={90}>90° — steps go right →</option>
                      <option value={180}>180° — steps go down ↓</option>
                      <option value={270}>270° — steps go left ←</option>
                    </select>
                  </div>
                )}
                <div style={s.panelDivider} />
                <button style={{ ...s.panelBtn, ...s.removePanelBtn }} onClick={()=>removeElement(editSelElem)}>
                  Remove
                </button>
              </div>
            ) : (
              <div style={s.panelEmpty}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:8}}>
                  <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                </svg>
                <div>Click a table or element to edit it</div>
              </div>
            )
          ) : (
            <div style={s.panelEmpty}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#cbd5e1" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{marginBottom:8}}>
                <path d="M3 6h18M3 12h18M8 6v12M16 6v12M3 18h18" />
              </svg>
              <div>Click a table to see reservations</div>
            </div>
          )}

          {/* Legend */}
          <div style={s.legend}>
            <div style={s.legendSection}>Areas</div>
            {areas.map(loc=>(
              <div key={loc} style={s.legendRow}>
                <div style={{ ...s.legendDot, background:LOC_COLOR[loc]||'#94a3b8' }} />
                <span>{loc.charAt(0).toUpperCase()+loc.slice(1)}</span>
              </div>
            ))}
            <div style={{ ...s.legendSection, marginTop:10 }}>Bookings</div>
            <div style={s.legendRow}><div style={{ ...s.legendDot, background:'#10b981', borderRadius:'50%' }} /><span>Booked today</span></div>
            <div style={s.legendRow}><div style={{ ...s.legendDot, background:'#f59e0b', borderRadius:'50%' }} /><span>Upcoming</span></div>
          </div>
        </div>
      </div>

      {/* View modal */}
      {viewTable && (
        <div style={s.overlay} onClick={()=>setViewTable(null)}>
          <div style={s.modal} onClick={e=>e.stopPropagation()}>
            <div style={s.modalTop}>
              <div>
                <h2 style={s.modalTitle}>{viewTable.label}</h2>
                <div style={s.modalSub}>
                  {viewTable.capacity} seats ·{' '}
                  <span style={{ color:LOC_COLOR[viewTable.location]||'#4f46e5', fontWeight:600, textTransform:'capitalize' }}>{viewTable.location}</span>
                </div>
              </div>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                {!booking&&!bookingSuccess && <button style={s.newBookingBtn} onClick={openBookingForm}>+ New Booking</button>}
                <button style={s.closeBtn} onClick={()=>setViewTable(null)}>×</button>
              </div>
            </div>
            <div style={s.divider} />
            {bookingSuccess && <div style={s.successBanner}>{bookingSuccess}</div>}
            {booking && (
              <div style={s.bookingForm}>
                <div style={s.bookingFormTitle}>New Booking — {viewTable.label}</div>
                {bookingError && <div style={s.bookingError}>{bookingError}</div>}
                <div style={s.bookingRow}>
                  <div style={s.bookingField}><label style={s.bookingLabel}>Date</label><input style={s.bookingInput} type="date" value={booking.date} onChange={e=>setB('date',e.target.value)} /></div>
                  <div style={s.bookingField}><label style={s.bookingLabel}>Time</label><input style={s.bookingInput} type="time" value={booking.time_slot} onChange={e=>setB('time_slot',e.target.value)} /></div>
                  <div style={s.bookingField}><label style={s.bookingLabel}>Party size</label><input style={s.bookingInput} type="number" min="1" max="50" value={booking.party_size} onChange={e=>setB('party_size',e.target.value)} /></div>
                </div>
                {booking.date&&booking.time_slot&&booking.party_size && (
                  <div style={{ fontSize:12, color:bookingChecking?'#94a3b8':bookingTables.find(t=>t.id===viewTable.id)?'#15803d':'#d97706', marginBottom:10 }}>
                    {bookingChecking?'Checking availability…':bookingTables.find(t=>t.id===viewTable.id)?'✓ Table is available':'Table may already be booked — booking will override'}
                  </div>
                )}
                <div style={s.bookingRow}>
                  <div style={s.bookingField}><label style={s.bookingLabel}>Customer name</label><input style={s.bookingInput} type="text" value={booking.customer_name} onChange={e=>setB('customer_name',e.target.value)} /></div>
                  <div style={s.bookingField}><label style={s.bookingLabel}>Phone or Email *</label><input style={s.bookingInput} type="text" value={booking.phone} onChange={e=>setB('phone',e.target.value)} /></div>
                </div>
                <div style={s.bookingField}><label style={s.bookingLabel}>Special requests</label><input style={s.bookingInput} type="text" value={booking.special_requests} onChange={e=>setB('special_requests',e.target.value)} /></div>
                <div style={{ display:'flex', gap:8, marginTop:4 }}>
                  <button style={s.saveBtn} onClick={submitBooking} disabled={bookingSaving}>{bookingSaving?'Creating…':'Create booking →'}</button>
                  <button style={s.cancelBtn} onClick={()=>{ setBooking(null); setBookingError(''); }}>Cancel</button>
                </div>
              </div>
            )}
            {!booking && (
              loadingResos ? <div style={{ color:'#94a3b8', padding:'20px 0' }}>Loading…</div>
              : tableResos.length===0 ? <div style={s.noResos}>No upcoming reservations for this table</div>
              : <>
                  <div style={s.resoHeader}>Upcoming reservations ({tableResos.length})</div>
                  <div style={{ display:'flex', flexDirection:'column', gap:0 }}>
                    {tableResos.map(r=>(
                      <div key={r.id} style={s.resoRow}>
                        <div style={s.resoDateCol}>
                          <div style={s.resoDate}>{new Date(r.date+'T12:00:00Z').toLocaleDateString('en-US',{ weekday:'short', month:'short', day:'numeric', timeZone:'UTC' })}</div>
                          <div style={s.resoTime}>{fmt(r.time_slot)}</div>
                        </div>
                        <div style={{ flex:1 }}>
                          <div style={s.resoName}>{r.customer_name}</div>
                          <div style={{ display:'flex', gap:6, flexWrap:'wrap', marginTop:3 }}>
                            <span style={s.chip}>{r.party_size} guests</span>
                            {r.phone && <span style={s.chip}>{r.phone}</span>}
                            {r.source==='ai' && <span style={{ ...s.chip, color:'#4f46e5', fontWeight:700 }}>AI</span>}
                          </div>
                          {r.special_requests && <div style={s.resoNote}>{r.special_requests}</div>}
                        </div>
                        <div style={{ ...s.statusDot, background:r.status==='confirmed'?'#10b981':'#f59e0b' }} title={r.status} />
                      </div>
                    ))}
                  </div>
                </>
            )}
          </div>
        </div>
      )}

      {/* Add table modal */}
      {showAdd && (
        <div style={s.overlay} onClick={()=>setShowAdd(false)}>
          <div style={{ ...s.modal, maxWidth:420 }} onClick={e=>e.stopPropagation()}>
            {showAdd==='unplaced' ? (
              <>
                <div style={s.modalTop}>
                  <div><h2 style={s.modalTitle}>Place Tables</h2><div style={s.modalSub}>These tables aren't on the floor plan yet.</div></div>
                  <button style={s.closeBtn} onClick={()=>setShowAdd(false)}>×</button>
                </div>
                <div style={s.divider} />
                <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
                  {tables.filter(t=>!t.floor_plan_placed).map(tbl=>(
                    <button key={tbl.id} style={s.unplacedRow} onClick={()=>placeTable(tbl)}>
                      <span style={{ fontWeight:700, color:'#0f172a', fontSize:14 }}>{tbl.label}</span>
                      <span style={{ fontSize:12, color:'#64748b' }}>{tbl.capacity} seats</span>
                      <span style={{ fontSize:12, color:LOC_COLOR[tbl.location]||'#4f46e5', fontWeight:600, textTransform:'capitalize', marginLeft:'auto' }}>{tbl.location}</span>
                      <span style={{ marginLeft:8, color:'#4f46e5', fontWeight:700 }}>+ Place →</span>
                    </button>
                  ))}
                </div>
              </>
            ) : (
              <>
                <h2 style={{ ...s.modalTitle, marginBottom:20 }}>New Table</h2>
                <div style={s.field}><label style={s.fieldLabel}>Label</label><input style={s.input} value={newTable.label} onChange={e=>setNewTable(p=>({...p,label:e.target.value}))} placeholder="e.g. T11" autoFocus /></div>
                <div style={s.field}><label style={s.fieldLabel}>Capacity</label><input style={s.input} type="number" min="1" value={newTable.capacity} onChange={e=>setNewTable(p=>({...p,capacity:e.target.value}))} /></div>
                <div style={s.field}><label style={s.fieldLabel}>Location</label><select style={s.input} value={newTable.location} onChange={e=>setNewTable(p=>({...p,location:e.target.value}))}>{LOCATIONS.map(l=><option key={l} value={l}>{l.charAt(0).toUpperCase()+l.slice(1)}</option>)}</select></div>
                <div style={{ display:'flex', gap:10 }}>
                  <button style={s.saveBtn} onClick={addTable}>Create table</button>
                  <button style={s.cancelBtn} onClick={()=>setShowAdd(false)}>Cancel</button>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {confirm && (
        <ConfirmModal
          message={confirm.message}
          detail={confirm.detail}
          confirmLabel="Remove"
          onConfirm={confirm.onConfirm}
          onCancel={() => setConfirm(null)}
        />
      )}
    </div>
  );
}

const s = {
  header:       { display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:14 },
  h1:           { fontSize:28, fontWeight:800, color:'#0f172a', margin:0, letterSpacing:'-0.5px' },
  sub:          { fontSize:14, color:'#64748b', marginTop:4 },
  editBtn:      { background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:10, padding:'10px 18px', fontSize:14, fontWeight:600, cursor:'pointer', color:'#334155' },
  editBtnActive:{ background:'#0f172a', borderColor:'#0f172a', color:'#fff' },
  addBtn:       { background:'#4f46e5', color:'#fff', border:'none', borderRadius:10, padding:'10px 18px', fontSize:14, fontWeight:700, cursor:'pointer' },
  badge:        { position:'absolute', top:-6, right:-6, background:'#f59e0b', color:'#fff', borderRadius:'50%', width:16, height:16, fontSize:10, fontWeight:700, display:'flex', alignItems:'center', justifyContent:'center' },
  savingText:   { fontSize:13, color:'#94a3b8' },
  savedText:    { fontSize:13, color:'#10b981', fontWeight:600 },

  zoomGroup:    { display:'flex', alignItems:'center', gap:0, background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:10, overflow:'hidden' },
  zoomBtn:      { background:'none', border:'none', width:30, height:32, fontSize:18, fontWeight:700, cursor:'pointer', color:'#334155', lineHeight:1 },
  zoomLabel:    { fontSize:12, fontWeight:600, color:'#475569', minWidth:38, textAlign:'center', userSelect:'none' },
  zoomReset:    { background:'none', border:'none', borderLeft:'1px solid #e2e8f0', padding:'0 8px', fontSize:11, fontWeight:600, cursor:'pointer', color:'#4f46e5', height:32 },

  elemToolbar:  { display:'flex', alignItems:'center', gap:8, marginBottom:12, flexWrap:'wrap' },
  elemToolbarLabel:{ fontSize:11, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em' },
  elemToolBtn:  { background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:8, padding:'7px 14px', fontSize:13, fontWeight:600, color:'#334155', cursor:'pointer' },
  elemToolbarHint:{ fontSize:11, color:'#94a3b8', marginLeft:4 },

  banner:       { display:'flex', alignItems:'center', justifyContent:'space-between', gap:12, borderRadius:12, padding:'11px 16px', marginBottom:12, fontSize:13 },
  bannerWarn:   { background:'#fffbeb', border:'1px solid #fde68a', color:'#92400e' },
  bannerInfo:   { background:'#eff6ff', border:'1px solid #bfdbfe', color:'#1e40af' },
  bannerOk:     { background:'#f0fdf4', border:'1px solid #bbf7d0', color:'#166534' },
  bannerBtn:    { background:'rgba(0,0,0,0.07)', border:'none', borderRadius:7, padding:'5px 12px', fontSize:12, fontWeight:700, cursor:'pointer', whiteSpace:'nowrap', color:'inherit' },

  areaTabs:     { display:'flex', gap:6, flexWrap:'wrap', marginBottom:14 },
  areaTab:      { display:'flex', alignItems:'center', background:'#fff', border:'1px solid #e2e8f0', borderRadius:20, padding:'6px 14px', fontSize:13, fontWeight:500, color:'#64748b', cursor:'pointer' },
  areaTabActive:{ background:'#f0f4ff', fontWeight:700 },

  layout:       { display:'flex', gap:16, alignItems:'flex-start' },
  canvasWrapper:{ flex:1, overflow:'auto', background:'#fff', border:'1px solid #e2e8f0', borderRadius:18, boxShadow:'0 1px 4px rgba(15,23,42,0.06)', height:'calc(100vh - 220px)', minHeight:400, display:'flex' },
  canvas:       { position:'relative', background:'#fff' },
  gridBg:       { position:'absolute', inset:0, pointerEvents:'none', backgroundImage:'radial-gradient(circle, #e2e8f0 1px, transparent 1px)', backgroundSize:`${GRID*2}px ${GRID*2}px` },

  table:        { position:'absolute', border:'2px solid', display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', userSelect:'none', touchAction:'none', transition:'box-shadow 0.15s, border-color 0.15s', zIndex:3 },
  tableLabel:   { fontSize:14, fontWeight:800, lineHeight:1 },
  tableCap:     { fontSize:10, color:'#94a3b8', marginTop:2 },
  locDot:       { width:6, height:6, borderRadius:'50%', marginTop:4 },
  resizeHandle: { position:'absolute', bottom:4, right:4, width:14, height:14, cursor:'nwse-resize', borderRight:'2px solid #cbd5e1', borderBottom:'2px solid #cbd5e1', borderRadius:'0 0 4px 0' },
  resoIndicator:{ position:'absolute', top:6, right:6, width:9, height:9, borderRadius:'50%', boxShadow:'0 0 0 2px #fff' },
  windowBadge:  { position:'absolute', bottom:5, left:5, fontSize:10, lineHeight:1, pointerEvents:'none' },

  wallHandle:   { position:'absolute', left:-7, top:-7, width:16, height:16, background:'#fff', border:'2.5px solid #4f46e5', borderRadius:'50%', cursor:'crosshair', zIndex:10, touchAction:'none' },

  panel:        { width:220, flexShrink:0, display:'flex', flexDirection:'column', gap:12 },
  panelInner:   { background:'#fff', border:'1px solid #e2e8f0', borderRadius:16, padding:'18px 16px', boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  panelTitle:   { fontSize:18, fontWeight:800, color:'#0f172a', marginBottom:2 },
  panelDivider: { height:1, background:'#f1f5f9', margin:'12px 0' },
  panelRow:     { display:'flex', justifyContent:'space-between', marginBottom:6 },
  panelKey:     { fontSize:11, color:'#94a3b8', fontWeight:600, textTransform:'uppercase' },
  panelVal:     { fontSize:12, color:'#334155', fontWeight:600 },
  panelField:   { marginBottom:10 },
  panelFieldLabel:{ display:'block', fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em', marginBottom:4 },
  panelInput:   { width:'100%', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'7px 10px', fontSize:13, outline:'none', boxSizing:'border-box', color:'#0f172a' },
  panelBtn:     { width:'100%', marginBottom:6, background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px', fontSize:13, cursor:'pointer', color:'#334155', fontWeight:500 },
  removePanelBtn:{ background:'#fef2f2', borderColor:'#fecaca', color:'#dc2626' },
  panelEmpty:   { background:'#fff', border:'1px solid #e2e8f0', borderRadius:16, padding:'32px 16px', textAlign:'center', color:'#94a3b8', fontSize:13, boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },

  legend:       { background:'#fff', border:'1px solid #e2e8f0', borderRadius:16, padding:'14px 16px', boxShadow:'0 1px 4px rgba(15,23,42,0.06)' },
  legendSection:{ fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.07em', marginBottom:6 },
  legendRow:    { display:'flex', alignItems:'center', gap:8, marginBottom:6, fontSize:12, color:'#475569' },
  legendDot:    { width:10, height:10, borderRadius:3, flexShrink:0 },

  overlay:      { position:'fixed', inset:0, background:'rgba(15,23,42,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:500 },
  modal:        { background:'#fff', border:'1px solid #e2e8f0', borderRadius:20, padding:'28px 32px', width:540, maxWidth:'92vw', maxHeight:'85vh', overflow:'auto', boxShadow:'0 20px 60px rgba(15,23,42,0.18)' },
  modalTop:     { display:'flex', justifyContent:'space-between', alignItems:'flex-start' },
  modalTitle:   { fontSize:20, fontWeight:800, color:'#0f172a', margin:'0 0 4px' },
  modalSub:     { fontSize:13, color:'#64748b' },
  newBookingBtn:{ background:'#4f46e5', color:'#fff', border:'none', borderRadius:8, padding:'8px 14px', fontSize:13, fontWeight:700, cursor:'pointer' },
  closeBtn:     { background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:8, width:32, height:32, fontSize:20, cursor:'pointer', color:'#475569', flexShrink:0 },
  divider:      { height:1, background:'#f1f5f9', margin:'16px 0' },
  successBanner:{ background:'#f0fdf4', color:'#15803d', border:'1px solid #bbf7d0', borderRadius:10, padding:'10px 14px', fontSize:13, marginBottom:14 },

  bookingForm:       { background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:14, padding:'18px 20px', marginBottom:16 },
  bookingFormTitle:  { fontSize:13, fontWeight:700, color:'#0f172a', marginBottom:14 },
  bookingError:      { background:'#fef2f2', color:'#dc2626', border:'1px solid #fecaca', borderRadius:8, padding:'8px 12px', fontSize:12, marginBottom:10 },
  bookingRow:        { display:'flex', gap:10, marginBottom:10 },
  bookingField:      { flex:1, display:'flex', flexDirection:'column', gap:4 },
  bookingLabel:      { fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.06em' },
  bookingInput:      { background:'#fff', border:'1px solid #e2e8f0', borderRadius:8, padding:'8px 10px', fontSize:13, outline:'none', width:'100%', boxSizing:'border-box', color:'#0f172a' },

  noResos:      { textAlign:'center', padding:'24px 0', color:'#94a3b8', fontSize:14 },
  resoHeader:   { fontSize:10, fontWeight:700, color:'#94a3b8', textTransform:'uppercase', letterSpacing:'0.08em', marginBottom:12 },
  resoRow:      { display:'flex', gap:14, alignItems:'flex-start', padding:'12px 0', borderBottom:'1px solid #f1f5f9' },
  resoDateCol:  { width:84, flexShrink:0 },
  resoDate:     { fontSize:12, fontWeight:700, color:'#0f172a' },
  resoTime:     { fontSize:11, color:'#64748b', marginTop:2 },
  resoName:     { fontSize:14, fontWeight:600, color:'#0f172a' },
  chip:         { background:'#f1f5f9', color:'#475569', borderRadius:6, padding:'2px 8px', fontSize:11, fontWeight:500 },
  resoNote:     { fontSize:12, color:'#94a3b8', marginTop:4 },
  statusDot:    { width:8, height:8, borderRadius:'50%', flexShrink:0, marginTop:4 },

  field:        { marginBottom:14 },
  fieldLabel:   { display:'block', fontSize:11, fontWeight:700, color:'#64748b', marginBottom:5, textTransform:'uppercase', letterSpacing:'0.06em' },
  input:        { width:'100%', background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:'10px 13px', fontSize:14, outline:'none', boxSizing:'border-box', color:'#0f172a' },
  saveBtn:      { flex:1, background:'#4f46e5', border:'none', borderRadius:10, padding:'11px 20px', fontSize:14, fontWeight:700, color:'#fff', cursor:'pointer' },
  cancelBtn:    { flex:1, background:'#f1f5f9', border:'1px solid #e2e8f0', borderRadius:10, padding:'11px 20px', fontSize:14, color:'#475569', cursor:'pointer' },
  unplacedRow:  { display:'flex', alignItems:'center', gap:10, background:'#f8fafc', border:'1px solid #e2e8f0', borderRadius:10, padding:'12px 14px', cursor:'pointer', textAlign:'left', width:'100%' },
};
